import { useContext } from "react";
import {
  Checkbox,
  CheckboxGroup,
  Disclosure,
  HeaderBar,
  HStack,
  InfoToggletip,
  SearchField,
  SymbolIcon,
  Tag,
  VStack,
} from "widgets";

import { useTrackPanel } from "./hooks/use-track-panel.ts";
import { TrackConfigurationContext } from "./TrackConfigurationContext.ts";
import style from "./TracksConfiguration.module.css";
import { updateTrackConfiguration, updateTrackConfigurationNu } from "./utils/update-track-configuration.ts";

export const TrackPanel = () => {
  const { trackConfiguration, setTrackConfiguration, selectedGroupIds, availableTrackConfiguration } =
    useContext(TrackConfigurationContext);

  const { selectedTracks, filteredTracks, selectedNuFeatures, filteredNuFeatureTracks, trackGroups, setFilterTerm } =
    useTrackPanel();

  return (
    <VStack spacing={0} className={style.trackPanel} data-testid="track-panel">
      <HeaderBar
        title="Tracks"
        asPanelHeader
        actions={[<SearchField key="search-field-tracks" onChange={setFilterTerm} width={120} />]}
      />
      {selectedGroupIds.length === 0 ? (
        <EmptyTrackSelection />
      ) : filteredTracks.length + filteredNuFeatureTracks.length === 0 ? (
        <EmptyTrackSearch />
      ) : (
        <div className={style.configList}>
          <CheckboxGroup
            value={selectedTracks}
            onChange={(selection) => {
              const newTrackConfiguration = updateTrackConfiguration(
                trackConfiguration,
                selectedGroupIds,
                selectedTracks,
                selection,
              );
              setTrackConfiguration(newTrackConfiguration);
            }}
          >
            {filteredTracks.map((track) => (
              <VStack
                key={track.type}
                className={style.trackItem}
                style={{
                  paddingLeft: 10,
                  borderRadius: 3,
                  margin: "0 10px",
                }}
              >
                <Checkbox
                  value={track.type}
                  // isDisabled={track.isDisabled}
                  isIndeterminate={
                    !Array.from(trackConfiguration)
                      .filter(([groupId]) => selectedGroupIds.includes(groupId))
                      .every(([_, trackFeatures]) => trackFeatures.defaultTypes.includes(track.type))
                  }
                >
                  <HStack
                    style={{
                      padding: "4px 5px",
                      justifyContent: "space-between",
                    }}
                  >
                    {track.name}
                  </HStack>
                </Checkbox>
              </VStack>
            ))}
          </CheckboxGroup>
          {filteredNuFeatureTracks.length > 0 && (
            <Disclosure
              header={
                <HStack>
                  DNA Features
                  <Tag label={filteredNuFeatureTracks.length} />
                </HStack>
              }
              style={{ marginTop: 20, marginLeft: 10 }}
            >
              <CheckboxGroup
                value={selectedNuFeatures.map((feature) => feature.id.toString())}
                onChange={(nuFeatureSelection) => {
                  const newTrackConfiguration = updateTrackConfigurationNu(
                    availableTrackConfiguration,
                    trackConfiguration,
                    selectedGroupIds,
                    nuFeatureSelection,
                  );
                  setTrackConfiguration(newTrackConfiguration);
                }}
              >
                {filteredNuFeatureTracks.map((track) => (
                  <VStack
                    key={track.id}
                    className={style.trackItem}
                    style={{
                      paddingLeft: 10,
                      borderRadius: 3,
                      margin: "0 10px",
                    }}
                  >
                    <Checkbox
                      expandToFill
                      value={track.id.toString()}
                      isIndeterminate={
                        !Array.from(trackConfiguration)
                          .filter(([groupId]) => selectedGroupIds.includes(groupId))
                          .every(([_, trackFeatures]) =>
                            trackFeatures.nuFeatureTypes.some((featureTrack) => featureTrack.id === track.id),
                          )
                      }
                    >
                      <HStack
                        style={{
                          padding: "4px 5px",
                          justifyContent: "space-between",
                        }}
                      >
                        {track.name}
                        <HStack>
                          {track.count === 0 && <span className={style.track_no_data}>(No data)</span>}
                          {selectedGroupIds.filter((groupId) =>
                            availableTrackConfiguration
                              .get(groupId)
                              ?.nuFeatureTypes.some((featureType) => featureType.id === track.id),
                          ).length !== selectedGroupIds.length && (
                            <InfoToggletip
                              content={
                                "This track type can only be applied to the following contig(s): " +
                                selectedGroupIds
                                  .filter((groupId) =>
                                    availableTrackConfiguration
                                      .get(groupId)
                                      ?.nuFeatureTypes.some((featureType) => featureType.id === track.id),
                                  )
                                  .map((groupId) => trackGroups.get(groupId)?.contigName)
                                  .join(", ")
                              }
                            />
                          )}
                        </HStack>
                      </HStack>
                    </Checkbox>
                  </VStack>
                ))}
              </CheckboxGroup>
            </Disclosure>
          )}
        </div>
      )}
    </VStack>
  );
};

const EmptyTrackSelection = () => {
  return (
    <div className={style.emptyTrackSelection}>
      <div>Select contig(s) in the left panel to see the available tracks</div>
      <SymbolIcon icon="reply" size={36} style={{ transform: "scaleY(-1)", color: "var(--primary-main)" }} />
    </div>
  );
};

const EmptyTrackSearch = () => {
  return (
    <div className={style.emptyTrackSelection}>
      <div>No tracks found</div>
    </div>
  );
};
